import _ from 'lodash';
import { useRootStore } from './index';
import { action, autorun, observable } from 'mobx';
import { ArticleQuery_article, AudiencesInput, FlexibleSpecInput, GeoLocationInput, KeyNameInput } from 'api';
import { Props as AutoCompleteProps } from 'pages/Article/components/FacebookCampaignArticle/components/AudienceManager/components/AttributeSelectors/components/Options/components/AsyncAutoComplete';
import { AudienceSourceEnum } from 'pages/Article/components/FacebookCampaignArticle/components/AudienceManager/constants/audienceSourceEnum';

interface CustomizesGeoLocations extends Omit<GeoLocationInput, 'countries'> {
  countries?: KeyNameInput[];
}

export interface CustomizesAudiencesInput
  extends Omit<AudiencesInput, 'locales' | 'geoLocations' | 'interests' | 'behaviors'> {
  geoLocations?: CustomizesGeoLocations;
  locales?: { key: string; name: number }[];
  interests?: { id: string; name: string; size: number }[];
  tableInterests?: { id: string; name: string; size: number }[];
  inputInterests?: { id: string; name: string; size: number }[];
  flexibleSpec?: FlexibleSpecInput[];
}

export class AudienceManagerStore {
  @observable article: ArticleQuery_article | null = null;
  @action setArticle = (article: ArticleQuery_article) => (this.article = article);

  @observable splitFields: string[] = [];
  @action setSplitFields = (fields: string[]) => (this.splitFields = fields);

  @observable audienceSource: AudienceSourceEnum | null = null;
  @action setAudienceSource = (source: AudienceSourceEnum) => (this.audienceSource = source);

  @observable creativeIds: number[] = [];
  @action setCreativeIds = (ids: number[]) => (this.creativeIds = ids);

  @observable audienceIds: number[] = [];
  @action setAudienceIds = (ids: number[]) => (this.audienceIds = ids);

  @observable openAudienceBuilderDialog = false;
  @action handleOpenAudienceBuilderDialog = () => (this.openAudienceBuilderDialog = true);
  @action handleCloseAudienceBuilderDialog = () => (this.openAudienceBuilderDialog = false);

  @observable activeStep = 1;
  @action setActiveStep = (step: number) => (this.activeStep = step);

  @observable disableStep = false;
  @action setDisableStep = (state: boolean) => (this.disableStep = state);

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  @observable testAudienceResult: any[] | null = null;
  /* eslint-disable @typescript-eslint/no-explicit-any */
  @action setTestAudienceResult = (result: any[] | null) => (this.testAudienceResult = result);

  @observable attributes: CustomizesAudiencesInput = {};
  @action setAttributes = (newAttributes: CustomizesAudiencesInput) => (this.attributes = newAttributes);

  @observable defaultAttributes: CustomizesAudiencesInput = {};
  @action setDefaultAttributes = (newAttributes: CustomizesAudiencesInput) => (this.defaultAttributes = newAttributes);

  @observable flexibleTargeting: AutoCompleteProps['context'][] = [];
  @action setFlexibleTargeting = (spec: AutoCompleteProps['context'][]) => (this.flexibleTargeting = spec);

  @action reset = () => {
    this.splitFields = [];
    this.flexibleTargeting = [];
    this.testAudienceResult = null;
    this.attributes = this.defaultAttributes;
  };

  runCheckForAudienceSelection = autorun(() => {
    if (this.audienceSource === AudienceSourceEnum.SIMILAR_STORIES) {
      if (this.activeStep === 1) {
        this.setDisableStep(_.isEmpty(this.audienceIds));
      }
    }
  });
}

export const useAudienceManagerStore = (): AudienceManagerStore => {
  const store = useRootStore();
  if (!store.audienceBuilderStore) throw new Error('CT store has not been initialized');
  return store.audienceBuilderStore;
};
